import { memo } from "react";

import { BillingModel, formatPrice } from "@workspace/billing";
import { isKey, useTranslation } from "@workspace/i18n";
import { cn } from "@workspace/ui";
import { Badge } from "@workspace/ui-web/badge";
import { Button } from "@workspace/ui-web/button";
import { Card } from "@workspace/ui-web/card";
import { Icons } from "@workspace/ui-web/icons";

import { usePlan } from "./hooks/use-plan";

import type { User } from "@workspace/auth";
import type {
  BillingConfigPlan,
  BillingConfigVariant,
} from "@workspace/billing";

const additionalDisplayedFeatures = [
  "pricing.additionalFeatures.editablePhraseLibrary",
  "pricing.additionalFeatures.languageSelection",
  "pricing.additionalFeatures.bannedWords",
];

const Price = ({ variant }: { variant: BillingConfigVariant }) => {
  const { t, i18n } = useTranslation(["common", "billing"]);

  const renderInterval = () =>
    !variant.custom && (
      <span className="text-muted-foreground shrink-0 text-lg">
        /{" "}
        {variant.model === BillingModel.RECURRING
          ? t(variant.interval)
          : t("lifetime")}
      </span>
    );

  return (
    <p className="relative flex items-end gap-1 py-2">
      <span className="text-4xl font-bold tracking-tighter md:text-5xl">
        {variant.custom
          ? isKey(variant.label, i18n, "billing")
            ? t(variant.label)
            : variant.label
          : formatPrice(
              {
                amount: variant.cost,
                currency: variant.currency,
              },
              i18n.language,
            )}
      </span>
      {renderInterval()}
    </p>
  );
};

interface PlanProps {
  readonly plan: BillingConfigPlan;
  readonly user: User | null;
}

export const Plan = memo<PlanProps>(({ plan, user }) => {
  const { t, i18n } = useTranslation(["common", "billing"]);

  const { features, isPending, handleCheckout, handleOpenPortal } =
    usePlan(plan);
  const displayedFeatures = [
    ...features,
    ...additionalDisplayedFeatures.map((feature) => ({
      id: feature,
      available: true,
      key: feature,
    })),
  ];

  return plan.variants.map((variant) => {
    return (
      <div className="min-w-0 rounded-lg" key={variant.id}>
        <Card
          className={cn(
            "relative flex h-full flex-col gap-6 px-6 pt-12 pb-6 sm:px-7 md:px-8 md:pt-12 md:pb-8",
            plan.badge && "border-primary",
          )}
        >
          {plan.badge && (
            <Badge className="hover:bg-primary absolute top-4 left-1/2 z-10 -translate-x-1/2 px-4 py-1.5 text-[11px] whitespace-nowrap uppercase shadow-sm">
              {isKey(plan.badge, i18n, "billing") ? t(plan.badge) : plan.badge}
            </Badge>
          )}

          <div>
            <span className="text-lg font-semibold">
              {isKey(plan.name, i18n, "billing") ? t(plan.name) : plan.name}
            </span>

            <Price variant={variant} />

            <p className="text-primary text-sm font-semibold sm:text-base">
              {t("pricing.trial.highlight")}
            </p>

            <p className="mt-3 text-sm leading-6">
              {isKey(plan.description, i18n, "billing")
                ? t(plan.description)
                : plan.description}
            </p>
          </div>

          <div className="flex flex-col gap-1">
            {displayedFeatures.map((feature) => (
              <div
                key={feature.id}
                className={cn("flex items-center gap-3 py-1", {
                  "opacity-50": !feature.available,
                })}
              >
                <div
                  className={cn(
                    "flex size-5 shrink-0 items-center justify-center rounded-full",
                    feature.available ? "bg-primary" : "border-primary border",
                  )}
                >
                  {feature.available ? (
                    <Icons.CheckIcon className="text-primary-foreground w-3" />
                  ) : (
                    <Icons.X className="text-primary w-3" />
                  )}
                </div>
                <span className="text-md">
                  {isKey(feature.key, i18n, "billing")
                    ? t(feature.key)
                    : feature.key}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-auto flex flex-col gap-3">
            <Button
              onClick={() =>
                variant.model === BillingModel.RECURRING
                  ? handleCheckout({
                      user,
                      variant: {
                        id: variant.id,
                        interval:
                          "interval" in variant ? variant.interval : null,
                      },
                    })
                  : handleOpenPortal({ user })
              }
              disabled={isPending}
              className="w-full"
            >
              {isPending ? (
                <Icons.Loader2 className="animate-spin" />
              ) : (
                t("pricing.cta.startFreeTrial")
              )}
            </Button>

            <p className="text-muted-foreground text-center text-xs leading-5 sm:text-sm">
              {t("pricing.trial.noteNoPayment")}
            </p>
          </div>
        </Card>
      </div>
    );
  });
});

Plan.displayName = "Plan";
